import { ReactNode } from "react";

import {
  RiFile3Line,
  RiFileExcelLine,
  RiFileImageLine,
  RiFilePdf2Line,
  RiFileTextLine,
  RiFileWordLine,
  RiSlideshow4Line,
} from "@remixicon/react";

export enum FileCategory {
  Document = "document",
  Image = "image",
  Pdf = "pdf",
}

export type FileTypeMeta = {
  mimeTypes: string[];
  label: string;
  extensions: string[];
  category: FileCategory;
};

export const wordDocType: FileTypeMeta = {
  mimeTypes: ["application/msword"],
  label: "Word Document (.doc)",
  extensions: ["doc"],
  category: FileCategory.Document,
};

export const wordDocxType: FileTypeMeta = {
  mimeTypes: [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  label: "Word Document (.docx)",
  extensions: ["docx"],
  category: FileCategory.Document,
};

export const excelXlsType: FileTypeMeta = {
  mimeTypes: ["application/vnd.ms-excel"],
  label: "Excel Spreadsheet (.xls)",
  extensions: ["xls"],
  category: FileCategory.Document,
};

export const excelXlsxType: FileTypeMeta = {
  mimeTypes: [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ],
  label: "Excel Spreadsheet (.xlsx)",
  extensions: ["xlsx"],
  category: FileCategory.Document,
};

export const pptType: FileTypeMeta = {
  mimeTypes: ["application/vnd.ms-powerpoint"],
  label: "PowerPoint Presentation (.ppt)",
  extensions: ["ppt"],
  category: FileCategory.Document,
};

export const pptxType: FileTypeMeta = {
  mimeTypes: [
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ],
  label: "PowerPoint Presentation (.pptx)",
  extensions: ["pptx"],
  category: FileCategory.Document,
};

export const txtType: FileTypeMeta = {
  mimeTypes: ["text/plain"],
  label: "Text File (.txt)",
  extensions: ["txt"],
  category: FileCategory.Document,
};

export const csvType: FileTypeMeta = {
  mimeTypes: ["text/csv"],
  label: "CSV File (.csv)",
  extensions: ["csv"],
  category: FileCategory.Document,
};

export const rtfType: FileTypeMeta = {
  mimeTypes: ["application/rtf"],
  label: "Rich Text File (.rtf)",
  extensions: ["rtf"],
  category: FileCategory.Document,
};

export const odtType: FileTypeMeta = {
  mimeTypes: ["application/vnd.oasis.opendocument.text"],
  label: "OpenDocument Text (.odt)",
  extensions: ["odt"],
  category: FileCategory.Document,
};

export const odsType: FileTypeMeta = {
  mimeTypes: ["application/vnd.oasis.opendocument.spreadsheet"],
  label: "OpenDocument Spreadsheet (.ods)",
  extensions: ["ods"],
  category: FileCategory.Document,
};

export const odpType: FileTypeMeta = {
  mimeTypes: ["application/vnd.oasis.opendocument.presentation"],
  label: "OpenDocument Presentation (.odp)",
  extensions: ["odp"],
  category: FileCategory.Document,
};

export const pdfType: FileTypeMeta = {
  mimeTypes: ["application/pdf"],
  label: "PDF Document (.pdf)",
  extensions: ["pdf"],
  category: FileCategory.Pdf,
};

export const jpegType: FileTypeMeta = {
  mimeTypes: ["image/jpeg"],
  label: "JPEG Image (.jpg, .jpeg)",
  extensions: ["jpg", "jpeg"],
  category: FileCategory.Image,
};

export const pngType: FileTypeMeta = {
  mimeTypes: ["image/png"],
  label: "PNG Image (.png)",
  extensions: ["png"],
  category: FileCategory.Image,
};

export const gifType: FileTypeMeta = {
  mimeTypes: ["image/gif"],
  label: "GIF Image (.gif)",
  extensions: ["gif"],
  category: FileCategory.Image,
};

export const bmpType: FileTypeMeta = {
  mimeTypes: ["image/bmp"],
  label: "Bitmap Image (.bmp)",
  extensions: ["bmp"],
  category: FileCategory.Image,
};

export const webpType: FileTypeMeta = {
  mimeTypes: ["image/webp"],
  label: "WebP Image (.webp)",
  extensions: ["webp"],
  category: FileCategory.Image,
};

export const tiffType: FileTypeMeta = {
  mimeTypes: ["image/tiff"],
  label: "TIFF Image (.tiff)",
  extensions: ["tiff"],
  category: FileCategory.Image,
};

export const allAllowedFileTypes: FileTypeMeta[] = [
  wordDocType,
  wordDocxType,
  excelXlsType,
  excelXlsxType,
  pptType,
  pptxType,
  txtType,
  csvType,
  rtfType,
  odtType,
  odsType,
  odpType,
  pdfType,
  jpegType,
  pngType,
  gifType,
  bmpType,
  webpType,
  tiffType,
];

export function getDocumentIcon(type?: string): ReactNode {
  switch (type?.toLocaleLowerCase()) {
    case "pdf":
      return <RiFilePdf2Line />;
    case "doc":
    case "docx":
    case "odt":
      return <RiFileWordLine />;
    case "xls":
    case "xlsx":
    case "ods":
    case "csv":
      return <RiFileExcelLine />;
    case "ppt":
    case "pptx":
      return <RiSlideshow4Line />;
    case "txt":
    case "rtf":
      return <RiFileTextLine />;
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "bmp":
    case "webp":
    case "tiff":
      return <RiFileImageLine />;
    default:
      return <RiFile3Line />;
  }
}
